import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import "../modeSelect/modeSelect.css";
import "./quizEditor.css";
import { selectAdminToken } from "../../sessionSlice";
import { generateQuizDraftAI } from "../../functions/generateQuizDraftAI";

const FIXED = { truefalse: ["True", "False"], mythfact: ["Myth", "Fact"] };
const VALID_FORMATS = ["mcq", "truefalse", "mythfact"];
const QUIZ_CSV_HEADERS = [
  "question_text",
  "format",
  "option1",
  "option2",
  "option3",
  "option4",
  "option5",
  "option6",
  "answer_index",
];

function escapeHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Shows multi-row CSV validation failures as a scrollable, left-aligned
// list — Swal's plain "text" option collapses newlines, so this builds the
// list as escaped HTML instead.
function showCsvErrors(errors) {
  const shown = errors.slice(0, 10);
  const more = errors.length > 10 ? `<div>...and ${errors.length - 10} more</div>` : "";
  const list = shown.map((e) => `<div>${escapeHtml(e)}</div>`).join("");
  Swal.fire({
    title: "CSV Error",
    html: `<div style="text-align:left;max-height:50vh;overflow:auto">${errors.length} row(s) failed validation:${list}${more}</div>`,
    icon: "error",
  });
}

// Shared content limits (enforced across all activity editors).
const MAX_QUESTIONS = 10;
const MAX_OPTIONS = 6;
const MAX_QUESTION_CHARS = 150;
const MAX_OPTION_CHARS = 70;
const IMG_W = 600;
const IMG_H = 400;
const VIDEO_MAX_BYTES = 10 * 1024 * 1024;
const AUDIO_MAX_BYTES = 2 * 1024 * 1024;

function blankQuestion(format = "mcq") {
  return {
    question_text: "",
    format,
    options: format === "mcq" ? ["", "", "", ""] : [...FIXED[format]],
    answer_index: 1,
    media_type: "none",
    media_url: null,
  };
}

const QuizEditor = () => {
  const navigate = useNavigate();
  const { activityId } = useParams();
  const adminToken = useSelector(selectAdminToken);
  const currentTheme = useSelector(
    (s) => s.theme?.currentTheme || s.theme?.data?.themename || null
  );
  const API = process.env.REACT_APP_BACKEND_URL;

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // AI modal state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiCount, setAiCount] = useState(5);
  const [aiFormat, setAiFormat] = useState("mcq");
  const [aiLoading, setAiLoading] = useState(false);

  const csvRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/pp2/getActivityContent`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ activity_id: activityId, currentTheme }),
        });
        const json = await res.json();
        if (json.success && Array.isArray(json.data.content)) {
          setQuestions(
            json.data.content.map((q) => ({
              question_text: q.question_text,
              format: q.format,
              options: q.options && q.options.length ? q.options : ["", ""],
              answer_index: q.answer_index,
              media_type: q.media_type || "none",
              media_url: q.media_url || null,
            }))
          );
        }
      } catch (e) {
        Swal.fire("Error", "Failed to load questions", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [activityId, adminToken, API, currentTheme]);

  const updateQ = (idx, patch) =>
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));

  const changeFormat = (idx, format) => {
    const q = questions[idx];
    const next = {
      format,
      options: format === "mcq" ? (q.format === "mcq" ? q.options : ["", "", "", ""]) : [...FIXED[format]],
      answer_index: 1,
    };
    updateQ(idx, next);
  };

  const changeOption = (idx, oi, value) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === idx ? { ...q, options: q.options.map((o, k) => (k === oi ? value : o)) } : q
      )
    );

  const addOption = (idx) =>
    setQuestions((prev) =>
      prev.map((q, i) => (i === idx && q.options.length < MAX_OPTIONS ? { ...q, options: [...q.options, ""] } : q))
    );

  const removeOption = (idx, oi) =>
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== idx || q.options.length <= 2) return q;
        const options = q.options.filter((_, k) => k !== oi);
        let answer_index = q.answer_index;
        if (answer_index > options.length) answer_index = options.length;
        return { ...q, options, answer_index };
      })
    );

  const addQuestion = () =>
    setQuestions((prev) => (prev.length >= MAX_QUESTIONS ? prev : [...prev, blankQuestion("mcq")]));
  const removeQuestion = (idx) => setQuestions((prev) => prev.filter((_, i) => i !== idx));

  const handleDeleteAllQuestions = async () => {
    if (questions.length === 0) return;
    const confirm = await Swal.fire({
      title: "Delete all questions?",
      text: `This removes all ${questions.length} question(s) in this quiz activity. This cannot be undone once saved.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d23b3b",
    });
    if (!confirm.isConfirmed) return;
    setQuestions([]);
  };

  // Enforce an exact 600x400 image so every quiz image renders identically.
  const checkImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.naturalWidth === IMG_W && img.naturalHeight === IMG_H) resolve();
        else
          reject(
            new Error(
              `Image must be exactly ${IMG_W}x${IMG_H}px. This file is ${img.naturalWidth}x${img.naturalHeight}px.`
            )
          );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Could not read image."));
      };
      img.src = url;
    });

  const handleMediaUpload = async (idx, type, file) => {
    if (!file) return;
    if (type === "image") {
      try {
        await checkImageDimensions(file);
      } catch (e) {
        Swal.fire("Wrong image size", e.message, "warning");
        return;
      }
    }
    if (type === "video" && file.size > VIDEO_MAX_BYTES) {
      Swal.fire("File too large", "Video must be 10 MB or smaller.", "warning");
      return;
    }
    if (type === "audio" && file.size > AUDIO_MAX_BYTES) {
      Swal.fire("File too large", "Audio must be 2 MB or smaller.", "warning");
      return;
    }
    const fd = new FormData();
    fd.append("media", file);
    fd.append("media_type", type);
    fd.append("currentTheme", currentTheme || "");
    try {
      const res = await fetch(`${API}/pp2/uploadQuizMedia`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Upload failed");
      updateQ(idx, { media_type: type, media_url: json.media_url });
    } catch (e) {
      Swal.fire("Error", e.message || "Media upload failed", "error");
    }
  };

  const validateAll = () => {
    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i];
      if (!q.question_text.trim()) return `Question ${i + 1}: text is required`;
      if (q.question_text.length > MAX_QUESTION_CHARS)
        return `Question ${i + 1}: question must be ${MAX_QUESTION_CHARS} characters or fewer`;
      const opts = q.options.map((o) => (o || "").trim());
      if (q.format === "mcq" && opts.filter(Boolean).length < 2)
        return `Question ${i + 1}: at least 2 options required`;
      if (q.format === "mcq" && q.options.length > MAX_OPTIONS)
        return `Question ${i + 1}: at most ${MAX_OPTIONS} options allowed`;
      if (opts.some((o) => o.length > MAX_OPTION_CHARS))
        return `Question ${i + 1}: each option must be ${MAX_OPTION_CHARS} characters or fewer`;
      if (q.answer_index < 1 || q.answer_index > q.options.length)
        return `Question ${i + 1}: select the correct answer`;
    }
    return null;
  };

  const handleSave = async () => {
    if (questions.length === 0) {
      Swal.fire("", "Add at least one question", "warning");
      return;
    }
    const err = validateAll();
    if (err) {
      Swal.fire("Fix required", err, "warning");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/pp2/saveQuizQuestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ activity_id: activityId, questions, currentTheme }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Save failed");
      Swal.fire("Saved", `${json.count} question(s) saved`, "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  // --- AI draft ---
  const openAiModal = () => {
    if (questions.length >= MAX_QUESTIONS) {
      Swal.fire("", `Maximum ${MAX_QUESTIONS} questions per activity. Delete some to generate more.`, "warning");
      return;
    }
    setAiCount(Math.min(aiCount, MAX_QUESTIONS - questions.length));
    setAiOpen(true);
  };

  const runAI = async () => {
    if (!aiTopic.trim()) {
      Swal.fire("", "Enter a topic", "warning");
      return;
    }
    const remaining = MAX_QUESTIONS - questions.length;
    if (remaining <= 0) {
      Swal.fire("", `Maximum ${MAX_QUESTIONS} questions per activity. Delete some to generate more.`, "warning");
      return;
    }
    setAiLoading(true);
    try {
      const draft = await generateQuizDraftAI({
        topic: aiTopic.trim(),
        count: Math.min(aiCount, remaining),
        format: aiFormat,
        existingTexts: questions.map((q) => q.question_text).filter(Boolean),
        token: adminToken,
      });
      if (!draft.length) throw new Error("No questions generated. Try again.");
      // Append the AI set to the existing draft (editable, never auto-saved),
      // capped at MAX_QUESTIONS. Nothing is persisted until the user hits
      // Save Questions.
      const added = draft.slice(0, remaining);
      setQuestions((prev) => [...prev, ...added]);
      setAiOpen(false);
      setAiTopic("");
      Swal.fire("Draft added", `${added.length} AI question(s) added for review. Hit Save Questions to keep these.`, "success");
    } catch (e) {
      Swal.fire("AI Error", e.message || "AI generation failed", "error");
    } finally {
      setAiLoading(false);
    }
  };

  // --- CSV import (validate -> editable) ---
  // Expected columns: question_text, format, option1..option6, answer_index
  // Enforces the exact same rules as the manual editor (validateAll above) —
  // bad rows are reported as errors rather than silently auto-corrected.
  const handleCsv = async (file) => {
    if (!file) return;
    const text = await file.text();
    try {
      const { headers, rows } = parseCsv(text);

      const blankHeaders = headers.filter((h) => !h.trim()).length;
      const unknownHeaders = headers.filter((h) => h.trim() && !QUIZ_CSV_HEADERS.includes(h));
      if (blankHeaders > 0) {
        throw new Error("CSV has one or more blank column headers.");
      }
      if (unknownHeaders.length) {
        throw new Error(
          `Unrecognized column(s): ${unknownHeaders.join(", ")}. Expected columns: ${QUIZ_CSV_HEADERS.join(", ")}.`
        );
      }

      const errors = [];
      const imported = [];

      rows.forEach((r, i) => {
        const rowNum = i + 2; // header is row 1

        const rawQuestionText = r.question_text || "";
        if (rawQuestionText !== rawQuestionText.trim()) {
          errors.push(`Row ${rowNum}: question_text has leading/trailing whitespace — remove it and re-upload`);
          return;
        }
        const question_text = rawQuestionText;
        if (!question_text) {
          errors.push(`Row ${rowNum}: question_text is required`);
          return;
        }
        if (question_text.length > MAX_QUESTION_CHARS) {
          errors.push(`Row ${rowNum}: question_text must be ${MAX_QUESTION_CHARS} characters or fewer`);
          return;
        }

        const rawFormat = r.format || "";
        if (rawFormat !== rawFormat.trim()) {
          errors.push(`Row ${rowNum}: format has leading/trailing whitespace — remove it and re-upload`);
          return;
        }
        const format = rawFormat.toLowerCase();
        if (!VALID_FORMATS.includes(format)) {
          errors.push(`Row ${rowNum}: format "${rawFormat}" is not valid — must be mcq, truefalse, or mythfact`);
          return;
        }

        let options;
        if (format === "mcq") {
          const rawOptions = [r.option1, r.option2, r.option3, r.option4, r.option5, r.option6].map((o) => o || "");
          const whitespaceAt = rawOptions.findIndex((o) => o !== o.trim());
          if (whitespaceAt !== -1) {
            errors.push(
              `Row ${rowNum}: option${whitespaceAt + 1} has leading/trailing whitespace — remove it and re-upload`
            );
            return;
          }
          if (rawOptions.filter(Boolean).length < 2) {
            errors.push(`Row ${rowNum}: at least 2 options required`);
            return;
          }
          const tooLong = rawOptions.find((o) => o.length > MAX_OPTION_CHARS);
          if (tooLong) {
            errors.push(`Row ${rowNum}: each option must be ${MAX_OPTION_CHARS} characters or fewer`);
            return;
          }
          // Keep positions as given (a blank in the middle is allowed — it's
          // only an error below if answer_index actually points at it) but
          // trim trailing blanks so the stored array isn't padded with
          // empty slots beyond the last real option.
          let lastFilled = -1;
          rawOptions.forEach((o, k) => {
            if (o) lastFilled = k;
          });
          options = rawOptions.slice(0, lastFilled + 1);
          if (options.length > MAX_OPTIONS) {
            errors.push(`Row ${rowNum}: at most ${MAX_OPTIONS} options allowed`);
            return;
          }
        } else {
          options = [...FIXED[format]];
        }

        const rawAnswerIndex = r.answer_index || "";
        if (rawAnswerIndex !== rawAnswerIndex.trim()) {
          errors.push(`Row ${rowNum}: answer_index has leading/trailing whitespace — remove it and re-upload`);
          return;
        }
        const answer_index = parseInt(rawAnswerIndex, 10);
        if (Number.isNaN(answer_index)) {
          errors.push(`Row ${rowNum}: answer_index is required and must be a number`);
          return;
        }
        if (answer_index < 1 || answer_index > options.length) {
          errors.push(`Row ${rowNum}: answer_index (${answer_index}) is out of range — this question has ${options.length} option slot(s)`);
          return;
        }
        if (!options[answer_index - 1]) {
          errors.push(`Row ${rowNum}: answer_index (${answer_index}) points to a blank option`);
          return;
        }

        imported.push({
          question_text,
          format,
          options,
          answer_index,
          media_type: "none",
          media_url: null,
        });
      });

      if (errors.length) {
        showCsvErrors(errors);
        return;
      }
      if (!imported.length) throw new Error("No valid rows found");

      const remaining = MAX_QUESTIONS - questions.length;
      if (imported.length > remaining) {
        throw new Error(
          `This CSV has ${imported.length} question(s), but only ${Math.max(0, remaining)} slot(s) remain (max ${MAX_QUESTIONS} per activity). Remove some rows from the CSV or delete existing questions, then re-upload.`
        );
      }
      setQuestions((prev) => [...prev, ...imported]);
      Swal.fire("Imported", `${imported.length} question(s) added for review. Edit, then Save.`, "success");
    } catch (e) {
      Swal.fire({ title: "CSV Error", text: e.message || "Could not parse CSV", icon: "error" });
    } finally {
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  // --- CSV export (same column format as import, for round-trip editing) ---
  const handleExportCsv = () => {
    if (questions.length === 0) {
      Swal.fire("", "No questions to export yet", "warning");
      return;
    }
    const headers = ["question_text", "format", "option1", "option2", "option3", "option4", "option5", "option6", "answer_index"];
    const rows = questions.map((q) => {
      const opts = q.format === "mcq" ? q.options : [];
      return [
        q.question_text || "",
        q.format || "mcq",
        opts[0] || "",
        opts[1] || "",
        opts[2] || "",
        opts[3] || "",
        opts[4] || "",
        opts[5] || "",
        q.answer_index || 1,
      ];
    });
    const csv = toCsv([headers, ...rows]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quiz_questions_${activityId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="load-theme-container">
        <div className="load-theme-spinner"></div>
        <p>Loading questions...</p>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/session/activities")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="rules-page-new">
        <div className="admin-tabs-holder-v2">
          <div className="tabs-main-title">Quiz Editor</div>

          <div className="qe-toolbar">
            <button className="qe-tool-btn" onClick={addQuestion} disabled={questions.length >= MAX_QUESTIONS}>
              <i className="fa-solid fa-plus"></i> Add Question
            </button>
            <button className="qe-tool-btn qe-ai" onClick={openAiModal}>
              <i className="fa-solid fa-wand-magic-sparkles"></i> Generate with AI
            </button>
            <button className="qe-tool-btn" onClick={() => csvRef.current?.click()}>
              <i className="fa-solid fa-file-csv"></i> Import CSV
            </button>
            <button className="qe-tool-btn" onClick={handleExportCsv}>
              <i className="fa-solid fa-file-export"></i> Export CSV
            </button>
            {questions.length > 0 && (
              <button className="qe-tool-btn qe-delete-all" onClick={handleDeleteAllQuestions}>
                <i className="fa-solid fa-trash"></i> Delete All
              </button>
            )}
            <input
              ref={csvRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={(e) => handleCsv(e.target.files?.[0])}
            />
          </div>

          <div className="qe-csv-hint code">
            CSV columns: question_text, format (mcq/truefalse/mythfact), option1..option6, answer_index
          </div>

          <div className="qe-count code">
            {questions.length} / {MAX_QUESTIONS} questions
          </div>

          {questions.length === 0 ? (
            <div className="admin-tab-container ab-empty">
              <i className="fa-solid fa-circle-question"></i>
              <p>No questions yet. Add manually, generate with AI, or import a CSV.</p>
            </div>
          ) : (
            questions.map((q, idx) => (
              <div className="admin-tab-container qe-card" key={idx}>
                <div className="qe-card-head">
                  <span className="qe-qnum">Q{idx + 1}</span>
                  <select
                    className="qe-format"
                    value={q.format}
                    onChange={(e) => changeFormat(idx, e.target.value)}
                  >
                    <option value="mcq">Multiple Choice</option>
                    <option value="truefalse">True / False</option>
                    <option value="mythfact">Myth / Fact</option>
                  </select>
                  <button className="qe-remove-q" onClick={() => removeQuestion(idx)}>
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>

                <div className="form-group-v1">
                  <label>Question</label>
                  <input
                    type="text"
                    value={q.question_text}
                    maxLength={MAX_QUESTION_CHARS}
                    onChange={(e) => updateQ(idx, { question_text: e.target.value })}
                    placeholder="Enter the question"
                  />
                  <div className="qe-hint code">Max {MAX_QUESTION_CHARS} characters</div>
                </div>

                {/* Media */}
                <div className="qe-media-row">
                  <label>Media:</label>
                  <select
                    value={q.media_type}
                    onChange={(e) => updateQ(idx, { media_type: e.target.value, media_url: e.target.value === "none" ? null : q.media_url })}
                  >
                    <option value="none">None</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                  </select>
                  {q.media_type !== "none" && (
                    <label className="qe-media-upload">
                      <input
                        type="file"
                        accept={
                          q.media_type === "image"
                            ? "image/*"
                            : q.media_type === "video"
                            ? "video/*"
                            : "audio/*"
                        }
                        style={{ display: "none" }}
                        onChange={(e) => handleMediaUpload(idx, q.media_type, e.target.files?.[0])}
                      />
                      <span className="qe-upload-chip">
                        <i className="fa-solid fa-upload"></i> {q.media_url ? "Replace" : "Upload"}
                      </span>
                    </label>
                  )}
                  {q.media_url && <span className="qe-media-ok"><i className="fa-solid fa-check"></i> attached</span>}
                  {q.media_type === "image" && (
                    <span className="qe-media-hint">Required size: {IMG_W}×{IMG_H}px</span>
                  )}
                </div>
                {q.media_url && q.media_type !== "none" && (
                  <div className="qe-media-preview">
                    {q.media_type === "image" && (
                      <img src={process.env.REACT_APP_S3_PATH + q.media_url} alt="" />
                    )}
                    {q.media_type === "video" && (
                      <video src={process.env.REACT_APP_S3_PATH + q.media_url} controls />
                    )}
                    {q.media_type === "audio" && (
                      <audio src={process.env.REACT_APP_S3_PATH + q.media_url} controls />
                    )}
                  </div>
                )}

                {/* Options */}
                <div className="qe-options">
                  <label>Options {q.format === "mcq" ? "(select the correct one)" : "(mark correct)"}</label>
                  {q.options.map((opt, oi) => (
                    <div className="qe-option-row" key={oi}>
                      <input
                        type="radio"
                        name={`correct-${idx}`}
                        checked={q.answer_index === oi + 1}
                        onChange={() => updateQ(idx, { answer_index: oi + 1 })}
                      />
                      <input
                        className="qe-option-input"
                        value={opt}
                        disabled={q.format !== "mcq"}
                        maxLength={MAX_OPTION_CHARS}
                        placeholder={`Option ${oi + 1}`}
                        onChange={(e) => changeOption(idx, oi, e.target.value)}
                      />
                      {q.format === "mcq" && q.options.length > 2 && (
                        <button className="qe-opt-remove" onClick={() => removeOption(idx, oi)}>
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      )}
                    </div>
                  ))}
                  {q.format === "mcq" && q.options.length < MAX_OPTIONS && (
                    <button className="add-btn" onClick={() => addOption(idx)}>
                      + Add Option
                    </button>
                  )}
                  <div className="qe-hint code">
                    Up to {MAX_OPTIONS} options, max {MAX_OPTION_CHARS} characters each
                  </div>
                </div>
              </div>
            ))
          )}

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Questions"}
            </button>
          </div>
        </div>
      </div>

      {/* AI modal */}
      {aiOpen && (
        <div className="ai-backdrop" onClick={() => !aiLoading && setAiOpen(false)}>
          <div className="ai-modal compact" onClick={(e) => e.stopPropagation()}>
            <h2 className="ai-title">Generate Quiz Draft</h2>
            {!aiLoading ? (
              <div className="ai-form">
                <label>Topic</label>
                <input className="ai-input" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g., Workplace Safety" />
                <label>Number of Questions</label>
                <select className="ai-input" value={aiCount} onChange={(e) => setAiCount(Number(e.target.value))}>
                  {[...Array(MAX_QUESTIONS - questions.length)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <div className="code">{MAX_QUESTIONS - questions.length} question slot(s) remaining in this activity.</div>
                <label>Format</label>
                <select className="ai-input" value={aiFormat} onChange={(e) => setAiFormat(e.target.value)}>
                  <option value="mcq">Multiple Choice</option>
                  <option value="truefalse">True / False</option>
                  <option value="mythfact">Myth / Fact</option>
                </select>
                <div className="code">AI output is an editable draft — review before saving.</div>
                <div className="generate-button-holder">
                  <button className="ai-generate-btn" onClick={runAI}>Generate Draft</button>
                </div>
              </div>
            ) : (
              <div className="ai-loading">
                <div className="ai-spinner"></div>
                <p>Generating draft...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Minimal CSV parser (handles quoted fields and commas within quotes).
// Returns both the raw header row (for validating column names) and the
// parsed rows (keyed by header).
function parseCsv(text) {
  const lines = text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseLine = (line) => {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i += 1) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else if (c === '"') {
          inQ = false;
        } else {
          cur += c;
        }
      } else if (c === '"') {
        inQ = true;
      } else if (c === ",") {
        out.push(cur);
        cur = "";
      } else {
        cur += c;
      }
    }
    out.push(cur);
    return out;
  };
  // Header names are matched case-insensitively, but NOT trimmed — a padded
  // header is a malformed column, not something to silently paper over (it
  // naturally surfaces as "unrecognized column" below). Row cell values are
  // kept completely raw — no trimming here at all. Leading/trailing
  // whitespace in actual content is flagged as a validation error by the
  // caller instead of being silently stripped.
  const headers = parseLine(lines[0]).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map((line) => {
    const cells = parseLine(line);
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cells[i] || "";
    });
    return row;
  });
  return { headers, rows };
}

// Serialize rows (array of arrays) to CSV text, quoting fields that contain
// commas, quotes, or newlines — mirrors parseCsv's quoting rules.
function toCsv(rows) {
  const escapeCell = (val) => {
    const s = String(val ?? "");
    if (/[",\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  return rows.map((row) => row.map(escapeCell).join(",")).join("\r\n");
}

export default QuizEditor;
